/*
 * Page-types settings — /{pub}/site-config/page-types.
 *
 * Two-column editor mirroring TreatmentTypesSettings (ticket 3.3): list of
 * pageTypes on the left, inline form on the right when a row is selected.
 *
 * Edits the pageTypeSetting artifact rows that ticket 2.3 backfilled. The
 * body is refs-only:
 *
 *   - treatmentTypeSettings: refs (by name) into the per-pub catalog
 *     (publicationSetting.treatmentTypes — 2.1c)
 *   - layouts:               refs (by id) into ArtifactType.Layout rows
 *   - contentTypes:          strings from the
 *     PublicationSettingSearchableContentType enum
 *
 * Save goes through the pageTypeSettingUpdate mutation. Switching rows
 * mid-edit shows an alertError and discards (same UX as 3.3).
 *
 * "Add new page type" is intentionally out of scope — see
 * /code/docs/layouts-dynamo-tickets/3.4-page-types-editor.md (3.4a follow-up).
 */

import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { ComboboxItem } from '@screentone/core';
import { Box, Button, Grid, Group, IconSave, MultiSelect, Typography, Wrapper } from '@screentone/core';

import { ResponsiveLoader } from 'components';
import { Accordion } from 'components/accordion/Accordion';
import { useAlert } from 'contexts/alert/useAlert';
import {
  ArtifactType,
  PageType,
  PublicationSettingSearchableContentType,
  useGetArtifactSettingsQuery,
  usePageTypeSettingsQuery,
  usePageTypeSettingUpdateMutation
} from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import styles from './PageTypesSettings.module.scss';

const ALL_CONTENT_TYPES = Object.values(PublicationSettingSearchableContentType) as string[];

interface DraftRefs {
  treatmentRefs: string[];
  layoutRefs: string[];
  contentTypes: string[];
}

const EMPTY_DRAFT: DraftRefs = {
  treatmentRefs: [],
  layoutRefs: [],
  contentTypes: []
};

function asItems(values: string[]): ComboboxItem[] {
  return values.map((v) => ({ id: v, label: v, value: v }));
}

interface PageTypeRow {
  pageType: PageType;
  treatmentTypeSettings: { treatmentType: string }[];
  layouts: { id: string }[];
  contentTypes?: PublicationSettingSearchableContentType[] | null;
}

function rowToDraft(row: PageTypeRow): DraftRefs {
  return {
    treatmentRefs: row.treatmentTypeSettings.map((r) => r.treatmentType),
    layoutRefs: row.layouts.map((l) => l.id),
    contentTypes: (row.contentTypes ?? []) as string[]
  };
}

const PageTypesSettings = () => {
  const { property } = useParams<{ property: string }>();
  const currentProperty = useConvertedProperty();
  const pubKey = property ?? currentProperty ?? '';

  // Data
  const {
    data: ptsData,
    isLoading,
    refetch
  } = usePageTypeSettingsQuery({ publicationKey: pubKey }, { enabled: !!pubKey });
  const { data: pubData } = usePublicationSettings({ publicationKey: pubKey });
  const { data: layoutsData } = useGetArtifactSettingsQuery(
    { publicationKey: pubKey, artifactType: ArtifactType.Layout },
    { enabled: !!pubKey }
  );

  const { alertError, alertSuccess } = useAlert();
  const { mutate, isLoading: isSaving } = usePageTypeSettingUpdateMutation();

  // Editing state
  const [editingKey, setEditingKey] = useState<PageType | null>(null);
  const [draft, setDraft] = useState<DraftRefs>(EMPTY_DRAFT);

  const rows = useMemo<PageTypeRow[]>(() => (ptsData?.pageTypeSettings ?? []) as PageTypeRow[], [ptsData]);

  const persistedDraft = useMemo<DraftRefs>(() => {
    if (!editingKey) return EMPTY_DRAFT;
    const row = rows.find((r) => r.pageType === editingKey);
    return row ? rowToDraft(row) : EMPTY_DRAFT;
  }, [editingKey, rows]);

  const isDirty = useMemo(
    () => editingKey !== null && JSON.stringify(draft) !== JSON.stringify(persistedDraft),
    [draft, editingKey, persistedDraft]
  );

  // Picker options
  const treatmentOptions = useMemo<ComboboxItem[]>(() => {
    const catalog = pubData?.publicationSetting.treatmentTypes ?? {};
    return asItems(Object.keys(catalog).sort());
  }, [pubData]);

  // Layout artifact rows are keyed by `{pageType}_{layoutId}`. Refs stored in
  // pageTypeSetting are the bare `layoutId`. Filter Layout rows to the
  // current pageType, then strip the prefix so the picker shows readable
  // ids (`home`, not `HOME_home`).
  const layoutOptions = useMemo<ComboboxItem[]>(() => {
    if (!editingKey) return [];
    const prefix = `${editingKey}_`;
    const layouts = layoutsData?.getArtifactSettings ?? [];
    return asItems(
      layouts
        .map((l) => l.artifactId)
        .filter((id) => id.startsWith(prefix))
        .map((id) => id.slice(prefix.length))
        .sort()
    );
  }, [layoutsData, editingKey]);

  const contentTypeOptions = useMemo<ComboboxItem[]>(() => asItems(ALL_CONTENT_TYPES), []);

  const missingTreatmentRefs = useMemo(
    () => draft.treatmentRefs.filter((ref) => !treatmentOptions.some((o) => o.value === ref)),
    [draft.treatmentRefs, treatmentOptions]
  );
  const missingLayoutRefs = useMemo(
    () => draft.layoutRefs.filter((id) => !layoutOptions.some((o) => o.value === id)),
    [draft.layoutRefs, layoutOptions]
  );

  // Sorted rows for the left column (alphabetical pageType name).
  const sortedRows = useMemo(() => [...rows].sort((a, b) => a.pageType.localeCompare(b.pageType)), [rows]);

  const openEdit = (key: PageType) => {
    if (editingKey === key) return;
    if (isDirty) {
      alertError(`Discarded unsaved changes to "${editingKey ?? ''}".`);
    }
    const row = rows.find((r) => r.pageType === key);
    setEditingKey(key);
    setDraft(row ? rowToDraft(row) : EMPTY_DRAFT);
  };

  const handleCancel = () => {
    setEditingKey(null);
    setDraft(EMPTY_DRAFT);
  };

  const handleSave = () => {
    if (!editingKey) return;
    mutate(
      {
        publicationKey: pubKey,
        pageType: editingKey,
        input: {
          treatmentTypeSettings: draft.treatmentRefs.map((treatmentType) => ({ treatmentType })),
          layouts: draft.layoutRefs.map((id) => ({ id })),
          contentTypes: draft.contentTypes as PublicationSettingSearchableContentType[]
        }
      },
      {
        onSuccess: () => {
          alertSuccess('Page-type setting saved.');
          void refetch();
        },
        onError: (err) => alertError(`Failed to save: ${err instanceof Error ? err.message : String(err)}`)
      }
    );
  };

  const updateDraft = <K extends keyof DraftRefs>(field: K, value: DraftRefs[K]) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleTreatmentChange = (items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])) => {
    const resolved = typeof items === 'function' ? items(asItems(draft.treatmentRefs)) : items;
    updateDraft(
      'treatmentRefs',
      resolved.map((it) => it.value as string)
    );
  };
  const handleLayoutChange = (items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])) => {
    const resolved = typeof items === 'function' ? items(asItems(draft.layoutRefs)) : items;
    updateDraft(
      'layoutRefs',
      resolved.map((it) => it.value as string)
    );
  };
  const handleContentTypesChange = (items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])) => {
    const resolved = typeof items === 'function' ? items(asItems(draft.contentTypes)) : items;
    updateDraft(
      'contentTypes',
      resolved.map((it) => it.value as string)
    );
  };

  if (!pubKey) {
    return null;
  }

  if (isLoading) {
    return <ResponsiveLoader />;
  }

  return (
    <Wrapper>
      <Typography margin={{ bottom: 'sm' }} variant="note">
        Configure which treatment types, layouts, and content types are available on each page type for this
        publication.
      </Typography>
      <Wrapper margin={{ bottom: 'lg' }}>
        <Accordion title="What is a page type?">
          <Typography variant="bodytext">
            A <strong>page type</strong> is a category of page on the site (e.g. <em>home</em>, <em>section</em>,{' '}
            <em>article</em>) that constrains what can appear on it. Each row below is the per-publication setting for
            one page type, controlling which treatment types editors can place on it, which layouts seed its structure,
            and which content types its modules accept. Edits here apply to every page of that type for this
            publication.
          </Typography>
        </Accordion>
      </Wrapper>

      <Grid>
        <Grid.Row>
          {/* Left column — page-type list */}
          <Grid.Col xs={12} sm={12} md={5} lg={5}>
            <Box>
              <Box.Title>Page types ({sortedRows.length})</Box.Title>
              <Box.Content>
                <Group gap="xs" direction="column" fullWidth>
                  {sortedRows.length === 0 ? (
                    <Typography variant="note">
                      No pageTypeSetting rows found for <strong>{pubKey}</strong>. Run backfill script.
                    </Typography>
                  ) : (
                    sortedRows.map((row) => (
                      <Button
                        key={row.pageType}
                        tertiary
                        className={editingKey === row.pageType ? styles.keyRowSelected : undefined}
                        onClick={() => openEdit(row.pageType)}
                      >
                        {row.pageType}
                      </Button>
                    ))
                  )}
                </Group>
              </Box.Content>
            </Box>
          </Grid.Col>

          {/* Right column — editor */}
          <Grid.Col xs={12} sm={12} md={7} lg={7}>
            <Box>
              <Box.Title>{editingKey ? `Edit: ${editingKey}` : 'Select a page type'}</Box.Title>
              <Box.Content>
                {editingKey ? (
                  <Group gap="lg" direction="column" fullWidth>
                    <Group gap="xs" direction="column" fullWidth>
                      <Typography variant="label2">Treatment types</Typography>
                      <Typography variant="note">
                        Refs into the per-pub treatment-types catalog (publicationSetting.treatmentTypes). Selections
                        must exist in the catalog — missing refs are listed below.
                      </Typography>
                      <MultiSelect
                        items={treatmentOptions}
                        selectedItems={asItems(draft.treatmentRefs)}
                        onSelectedItemsChange={handleTreatmentChange}
                        status="ready"
                        emptyContent={
                          treatmentOptions.length === 0
                            ? `No treatment types in the catalog for ${pubKey}. Run backfill.`
                            : `All ${treatmentOptions.length} available treatment type(s) are selected.`
                        }
                        data-testid="treatment-types-multi-select"
                      />
                      {missingTreatmentRefs.length > 0 && (
                        <Typography variant="note" color="lava" margin={{ top: 'sm' }}>
                          ⚠ {missingTreatmentRefs.length} ref(s) not in the catalog: {missingTreatmentRefs.join(', ')}.
                          Re-run backfill for this pub.
                        </Typography>
                      )}
                    </Group>

                    <Group gap="xs" direction="column" fullWidth>
                      <Typography variant="label2">Layouts</Typography>
                      <Typography variant="note">
                        Refs to layout artifact rows for this pub. Order determines display order.
                      </Typography>
                      <MultiSelect
                        items={layoutOptions}
                        selectedItems={asItems(draft.layoutRefs)}
                        onSelectedItemsChange={handleLayoutChange}
                        status="ready"
                        emptyContent={
                          layoutOptions.length === 0
                            ? `No layouts found for ${editingKey} on ${pubKey}. Add ArtifactType.Layout rows first.`
                            : `All ${layoutOptions.length} available ${editingKey} layout(s) are selected.`
                        }
                        data-testid="layouts-multi-select"
                      />
                      {missingLayoutRefs.length > 0 && (
                        <Typography variant="note" color="lava" margin={{ top: 'sm' }}>
                          ⚠ {missingLayoutRefs.length} layout ref(s) not present as ArtifactType.Layout rows:{' '}
                          {missingLayoutRefs.join(', ')}.
                        </Typography>
                      )}
                    </Group>

                    <Group gap="xs" direction="column" fullWidth>
                      <Typography variant="label2">Content types</Typography>
                      <Typography variant="note">Allowed content types on pages of this pageType.</Typography>
                      <MultiSelect
                        items={contentTypeOptions}
                        selectedItems={asItems(draft.contentTypes)}
                        onSelectedItemsChange={handleContentTypesChange}
                        status="ready"
                        data-testid="content-types-multi-select"
                      />
                    </Group>

                    <Group gap="md" direction="row" align="end" fullWidth>
                      <Button tertiary onClick={handleCancel} disabled={isSaving}>
                        Cancel
                      </Button>
                      <Button primary disabled={!isDirty || isSaving} onClick={handleSave} icon={IconSave}>
                        {isSaving ? 'Saving…' : 'Save'}
                      </Button>
                    </Group>
                  </Group>
                ) : (
                  <Typography variant="note">Click a page type on the left to edit.</Typography>
                )}
              </Box.Content>
            </Box>
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </Wrapper>
  );
};

export default PageTypesSettings;
